import { useMachine } from '@xstate/react';
import { useMemo } from 'react';
import { filter, from, map, Observable, Subject } from 'rxjs';
import { createMachine, State } from 'xstate';
import { environment } from '../../config';
import { useSubscription } from '../hooks/useSubscription';
import { BusEvent } from './EditorContext';

export type EditorEvent =
  | { type: 'FETCHED_DRAFT' }
  | { type: 'FETCH_FAILED' }
  | { type: 'INPUT' }
  | { type: 'INPUT_END' }
  | { type: 'LETTER_OUTDATED' }
  | { type: 'IDLE_TIME_PASSED' }
  | { type: 'SYNC_SUCCESSFUL' }
  | { type: 'SYNC_FAILED' }
  | { type: 'SYNC_LOCAL' };

export type EditorValueState =
  | { value: 'fetching' }
  | { value: 'fetchError' }
  | { value: 'editing' }
  | { value: 'syncing' }
  | { value: 'syncError' }
  | { value: 'saved' }
  | { value: 'idling' }
  | { value: 'reloading' };

export type EditorState = EditorValueState & { context: {} };

const fsm = createMachine<{}, EditorEvent, EditorState>({
  id: 'Draft Editor',
  initial: 'fetching',
  states: {
    fetching: { on: { FETCHED_DRAFT: 'saved', FETCH_FAILED: 'fetchError' } },
    fetchError: { on: { FETCHED_DRAFT: 'saved' } },
    saved: { on: { INPUT: 'editing', IDLE_TIME_PASSED: 'idling' } },
    editing: { on: { INPUT: 'editing', INPUT_END: 'syncing' } },
    syncing: { on: { INPUT: 'editing', SYNC_SUCCESSFUL: 'saved', SYNC_FAILED: 'syncError' } },
    syncError: { on: { INPUT: 'editing', SYNC_LOCAL: 'saved' } },
    idling: { on: { INPUT: 'editing', LETTER_OUTDATED: 'reloading' } },
    reloading: { on: { FETCHED_DRAFT: 'saved', FETCH_FAILED: 'fetchError' } },
  },
});

export type EditorMachineState = State<any, EditorEvent, any, EditorState>;
export type EditorMachineDispatch = (e: EditorEvent) => void;
export type State$ = Observable<EditorMachineState>;

const reportState = ({ value, event }: EditorMachineState) => {
  environment === 'development' && console.log('Editor', event, '=>', value);
};

export type EditorMachine = {
  state: EditorMachineState;
  send: EditorMachineDispatch;
  state$: State$;
};

export const useEditorMachine = (bus$: Subject<BusEvent>) => {
  const [state, send, service] = useMachine<any, EditorEvent, EditorState>(fsm);
  const state$ = useMemo(() => from(service).pipe(), [service]);

  useSubscription(() => state$.subscribe(reportState), [state$]);

  const editorEvent$ = useMemo(
    () =>
      bus$.pipe(
        filter((e) => e.type === 'Editor'),
        map((e) => (e as { type: 'Editor'; event: EditorEvent }).event)
      ),
    [bus$]
  );

  useSubscription(() => editorEvent$.subscribe((e) => send(e)), [editorEvent$, send]);

  return { state, send, state$ };
};
